<template>
  <div class="w-full" id="AboutYourself">
    <ValidationObserver tag="form" ref="AboutYourself" v-slot="observer">
      <h2 class="mb-5 text-2xl font-bold text-center lg:text-4xl">About you</h2>
      <AggregatorEditRow :required="true" :complete="isNameSectionComplete" title="Name" :preview="`${title} ${firstName} ${lastName}`">
          <div class="mx-auto mt-2 mb-8">
              <ValidationProvider name="Title" ref="title" rules="required">
                <div slot-scope="{ errors }">
                  <fieldset>
                    <legend class="font-semibold label" for="title">Title</legend>
                    <div class="inline" v-for="titleEntry in salutations" :key="titleEntry">
                      <input type="radio" name="title" v-model="title" :id="titleEntry" :value="capatilise(titleEntry)"/>
                      <label @keypress.enter.prevent="$event.target.click" tabindex="0"  :class="{ 'border-error': errors[0] }" class="label-select" :for="titleEntry">
                        {{ capatilise(titleEntry) }}
                        <TickIcon class="tick-icon"/>
                      </label>
                    </div>
                  </fieldset>
                  <p :class="{ 'pt-2': errors[0] }" class="text-polred">{{ errors[0] }}</p>
                </div>
              </ValidationProvider>
          </div>
          <div class="flex flex-wrap -mx-3">
            <div class="w-full px-3 mb-4 md:w-1/2 md:mb-0">
              <!-- first name -->
              <ValidationProvider name="First name" ref="firstName" rules="required|min:2|max:32">
                <div slot-scope="{ errors }">
                  <label class="font-semibold label" for="firstname">First name</label>
                  <input tabindex="0" v-model="firstName" :class="{ 'border-error': errors[0] }" class="input focus:outline-none" id="firstname" type="text" autocomplete="off">
                  <p class="text-polred">{{ errors[0] }}</p>
                </div>
              </ValidationProvider>
            </div>
            <div class="w-full px-3 md:w-1/2">
              <!-- last name -->
              <ValidationProvider name="Last name" ref="lastName" rules="required|min:2|max:32">
                <div slot-scope="{ errors }">
                  <label class="font-semibold label" for="lastname">Last name</label>
                  <input tabindex="0" v-model.trim="lastName" :class="{ 'border-error': errors[0] }" class="input focus:outline-none" id="lastname" type="text" autocomplete="off">
                  <p class="text-polred">{{ errors[0] }}</p>
                </div>
              </ValidationProvider>
            </div>
        </div>
      </AggregatorEditRow>
      <AggregatorEditRow :required="true" title="Date of Birth" :complete="isDOBSectionComplete" :preview="dobDisplayView">
          <p class="font-semibold label" for="dateofbirth">Date of Birth</p>
          <div class="flex flex-wrap mb-2 -mx-3">
            <div class="w-full px-3 mb-6 md:w-1/3 md:mb-0">
              <div class="relative">
                <!-- day of birth -->
                <ValidationProvider ref="dayOfBirth" name="Day" rules="required|numeric|between:1,31">
                  <div slot-scope="{ errors }">
                    <label class="font-semibold label" for="dayOfBirth">Day</label>
                    <input tabindex="0" placeholder="Day" v-model.trim="dayOfBirth" :class="{ 'border-error': errors[0] }" class="placeholder-polgrey-dark input focus:outline-none" id="dayOfBirth" type="number">
                    <p class="text-polred">{{ errors[0] }}</p>
                  </div>
                </ValidationProvider>
              </div>
            </div>
            <div class="w-full px-3 mb-6 md:w-1/3 md:mb-0">
              <div class="relative">
                <!-- month of birth -->
                <ValidationProvider ref="monthOfBirth" name="Month" rules="required|numeric|between:1,12">
                  <div slot-scope="{ errors }">
                    <label class="font-semibold label" for="monthOfBirth">Month</label>
                    <input tabindex="0" placeholder="Month" v-model.trim="monthOfBirth" :class="{ 'border-error': errors[0] }" class="placeholder-polgrey-dark input focus:outline-none" id="monthOfBirth" type="number" autocomplete="off">
                    <p class="text-polred">{{ errors[0] }}</p>
                  </div>
                </ValidationProvider>
              </div>
            </div>
            <div class="w-full px-3 mb-6 md:w-1/3 md:mb-0">
              <div class="relative">
                <!-- year of birth -->
                <ValidationProvider ref="yearOfBirth" name="Year" rules="required|numeric|between:1900,2020">
                  <div slot-scope="{ errors }">
                    <label class="font-semibold label" for="yearOfBirth">Year</label>
                    <input tabindex="0" placeholder="Year" v-model.trim="yearOfBirth" :class="{ 'border-error': errors[0] }" class="placeholder-polgrey-dark input focus:outline-none" id="yearOfBirth" type="number" autocomplete="off">
                    <p class="text-polred">{{ errors[0] }}</p>
                  </div>
                </ValidationProvider>
              </div>
            </div>
        </div>
      </AggregatorEditRow>
      <AggregatorEditRow :required="true" :complete="isEmailSectionComplete" title="Email" :preview="`${emailAddress}`">
          <div class="flex flex-wrap mt-2 -mx-3">
            <div class="w-full px-3">
              <!-- email address -->
              <ValidationProvider name="Email" ref="emailAddress" rules="required|email|min:1|max:50">
                <div slot-scope="{ errors }">
                  <label class="font-semibold label" for="email">Email</label>
                  <input tabindex="0" v-model.trim="emailAddress" :class="{ 'border-error': errors[0] }" class="placeholder-polgrey-dark input focus:outline-none" id="email" type="email" autocomplete="off" placeholder="">
                  <p class="text-polred">{{ errors[0] }}</p>
                </div>
              </ValidationProvider>
            </div>
          </div>
      </AggregatorEditRow>
      <AggregatorEditRow :required="mobilePhoneNumber.length > 0" title="Mobile number" :complete="isMobileNumberComplete" :preview="`${mobilePhoneNumber}`">
        <div class="flex flex-wrap mt-4 -mx-3">
          <div class="w-full px-3 mx-auto mb-4 lg:w-4/12 md:w-auto md:mb-0">
            <!-- mobile number -->
            <label class="font-semibold label" for="mobilePhoneNumber">Mobile number</label>
            <ValidationProvider ref="mobilePhoneNumber" name="Mobile number" :rules="mobileFieldRules">
              <div slot-scope="{ errors }">
                <input tabindex="0" v-model.trim="mobilePhoneNumber" :class="{ 'border-error': errors[0] }" class="input focus:outline-none" id="mobilePhoneNumber" type="tel" autocomplete="off">
                <p class="text-polred">{{ errors[0] }}</p>
              </div>
            </ValidationProvider>
          </div>
        </div>
      </AggregatorEditRow>
    </ValidationObserver>
  </div>
</template>

<script>

import TickIcon from '@/v2/assets/images/icons/select-tick.svg'

import { mapFields } from 'vuex-map-fields'
import AggregatorEditRow from '@/v2/components/elements/AggregatorFlow/EditRow'

import mixins from '@/v2/mixins'
import { salutations } from '../../../form'

export default {
  name: 'AboutYourself',
  mixins: [mixins],
  data() {
    return {
      salutations,
      yearOfBirth: '',
      monthOfBirth: '',
      dayOfBirth: '',
      isNameSectionComplete: false,
      isDOBSectionComplete: false,
      isEmailSectionComplete: false,
      isMobileNumberComplete: false,
      mobileFieldRules: '',
    }
  },
  components: {
    TickIcon,
    AggregatorEditRow
  },
  created() {
    this.$root.$on('dob:split', () => { this.splitDateOfBirthFieldsAndDisplay() })
  },
  methods: {},
  computed : {
    dobDisplayView() {
      
      let date = new Date(`${this.yearOfBirth}-${this.monthOfBirth}-${this.dayOfBirth}`)
      let string = `${this.zeroPad(this.dayOfBirth, 2)}/${this.zeroPad(this.monthOfBirth, 2)}/${this.zeroPad(this.yearOfBirth, 2)}`

      if (date) {
        string = `${string} (Age: ${this.calculateAge(date)})`
      }

      return string
    },
    nameFields() {
      return this.title, this.firstName, this.lastName, Date.now()
    },
    isSectionComplete() {
      return this.isNameSectionComplete, this.isDOBSectionComplete, this.isEmailSectionComplete, this.isMobileNumberComplete, Date.now()
    },
    emailAddressField() {
      return this.emailAddress, Date.now()
    },
    dateOfBirthFields() {
      return this.yearOfBirth, this.monthOfBirth, this.dayOfBirth, Date.now()
    },
    mobileField() {
      return this.mobilePhoneNumber, Date.now()
    },
    ...mapFields([
      'person.title',
      'person.firstName',
      'person.lastName',
      'person.emailAddress',
      'person.birthDate',
      'person.mobilePhoneNumber',
    ])
  },
  watch: {
    isSectionComplete: {
      immediate: true,
      async handler() {
        this.$nextTick(async () => {
          let completeStatus = (this.isNameSectionComplete && this.isDOBSectionComplete && this.isEmailSectionComplete)
          if (this.mobilePhoneNumber && this.mobilePhoneNumber.length > 0) {
            await this.$root.$emit(`aboutYourSelfComplete`, this.isNameSectionComplete && this.isDOBSectionComplete && this.isEmailSectionComplete && this.isMobileNumberComplete)
          } else {
            await this.$root.$emit(`aboutYourSelfComplete`, completeStatus)
          }
        })
      },
    },
    dateOfBirthFields: {
      immediate: true,
      async handler() {
        this.$nextTick(async () => {
          this.birthDate = `${this.zeroPad(this.yearOfBirth, 2)}-${this.zeroPad(this.monthOfBirth, 2)}-${this.zeroPad(this.dayOfBirth, 2)}`
          let isDayValid = await this.validateField(this.$refs, 'dayOfBirth')
          let isMonthValid = await this.validateField(this.$refs, 'monthOfBirth')
          let isYearValid = await this.validateField(this.$refs, 'yearOfBirth')
          
          this.isDOBSectionComplete = (isDayValid && isMonthValid && isYearValid)
        })
      },
    },
    nameFields: {
       immediate: true,
       async handler() {
         this.$nextTick(async () => {
            this.isNameSectionComplete = (
              await this.validateField(this.$refs, 'title') &&
              await this.validateField(this.$refs, 'firstName') &&
              await this.validateField(this.$refs, 'lastName'))
         })
        },
    },
    emailAddressField: {
      immediate: true,
      async handler() {
        this.$nextTick(async () => {
          this.isEmailSectionComplete = await this.validateField(this.$refs, 'emailAddress')
        })
      }
    },
    mobileField: {
      immediate: true,
      async handler() {
          if (this.mobilePhoneNumber && this.mobilePhoneNumber.length > 0) {
            this.mobileFieldRules = 'required|min:1|max:11|numeric'
            this.$nextTick(async () => {
              this.isMobileNumberComplete = await this.validateField(this.$refs, 'mobilePhoneNumber')
            })
          } else {
            this.mobileFieldRules = ''
            this.isMobileNumberComplete = false
          }
      }
    },
  }
}
</script>